// ClubeInsigne.js — a página de venda do Clube (rota /clube).
//
// Ela responde, nesta ordem:
//   1. quanto acervo a assinatura destrava — o argumento mais forte que a
//      oferta tem, e por isso é o maior número da tela;
//   2. quanto custa comparado com comprar UM livro, com a conta feita à vista;
//   3. o que trava a decisão ("isso vai me cobrar todo mês?"), respondido ao
//      lado do botão e não no rodapé;
//   4. para quem já assina: até quando vale e como renovar sem perder dias.
//
// ================= POR QUE ELA FOI REFEITA (04/09/2026) =================
//
// O pedido foi "está simples demais". O diagnóstico foi outro: a matéria-prima
// já existia e estava escondida. O tamanho do acervo era uma linha de rodapé
// cinza; as seis vantagens tinham exatamente o mesmo peso visual, e seis coisas
// com o mesmo peso é o mesmo que nenhuma em destaque — ninguém lê a parede
// inteira; a comparação de preço era implícita; "não renova sozinha", que é o
// maior removedor de objeção da oferta, era a última linha da página; e não
// havia uma capa sequer, num site de editora, onde a capa É o produto.
//
// Nenhum argumento novo foi inventado aqui. O que mudou foi a hierarquia.
//
// ================= OS PREÇOS VÊM DO BACKEND =================
//
// Nada de valor escrito à mão neste arquivo. O preço da assinatura já vive em
// config/insigne.php e o do coin em config/coins.js; uma terceira cópia no JSX
// seria a que diverge no dia em que alguém mudar o valor em dois dos três
// lugares — e esta é a única tela em que o preço velho custa a venda inteira.
// Enquanto o número não chega, o lugar dele fica um esqueleto: "R$ 0,00" numa
// página de venda lê como defeito ou como promessa de gratuidade.
//
// A ASSINATURA NÃO É RENOVADA AUTOMATICAMENTE, e a tela diz isso onde a pessoa
// decide, não no rodapé: "não renova sozinha" é o tipo de informação que, se
// aparecer depois da compra, vira reclamação.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../services/context/AuthContext";
import useClubeVitrine from "../../hooks/useClubeVitrine";
import { message } from "../utils/toast";
import Reveal from "../ui/Reveal";
import RevealStagger from "../ui/RevealStagger";
import AcervoDoClube from "./AcervoDoClube";
import TarjaDesconto from "../ui/TarjaDesconto";
import useConfirmarGasto, { opcoesUsarTicket } from "../ui/ConfirmarGasto";
import { Giros } from "../ui/TituloDeSecao";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

// "Tickets" e "Coins" são os NOMES dos dois créditos: inicial maiúscula e a
// cor de cada um, em toda frase desta página (pedido do dono, 11/09/2026 — a
// mesma regra da Loja de Créditos do perfil). Uma função para cada, e não a
// palavra escrita à mão, porque é assim que ela volta minúscula e branca num
// canto que ninguém revisou.
const TicketTxt = ({ um = false }) => (
  <span className="font-semibold text-insigne-ticket">{um ? "Ticket" : "Tickets"}</span>
);
const CoinsTxt = () => <span className="font-semibold text-insigne-orange">Insigne Coins</span>;

const reais = (centavos) =>
  "R$ " + ((Number(centavos) || 0) / 100).toFixed(2).replace(".", ",");

const fmtData = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

// O preço de UM e-book, para a comparação com a assinatura.
//
// MEDIANA, e não média: uma coletânea cara ou um lançamento em promoção puxam a
// média e fazem a comparação mentir — para mais ou para menos. A mediana é o
// título do meio, e é ela que a tela pode chamar de "preço típico" sem mentir.
// A palavra "média" não aparece na tela de propósito: seria outra conta.
//
// A amostra são os lançamentos recentes, que é o que a rota pública devolve.
// Não é o catálogo inteiro, e por isso a tela diz "entre os lançamentos
// recentes" em vez de fingir que é a média da editora. Sem nenhum preço na
// amostra o bloco da comparação some — não se inventa valor de referência.
const medianaCentavos = (livros) => {
  const valores = (livros || [])
    .map((l) => Math.round(Number(l?.preco_com_desconto ?? l?.preco ?? 0) * 100))
    .filter((c) => c > 0)
    .sort((a, b) => a - b);

  if (!valores.length) return 0;

  const meio = Math.floor(valores.length / 2);
  return valores.length % 2
    ? valores[meio]
    : Math.round((valores[meio - 1] + valores[meio]) / 2);
};

// AS DUAS QUE CARREGAM A OFERTA. Uma é o acervo (o que se compra), a outra é o
// leitor (o motivo de ler aqui em vez de baixar um PDF). Ganham cartão grande
// porque, se a pessoa só ler duas linhas da página, precisam ser estas.
const VANTAGENS_DESTAQUE = [
  {
    icone: "bi-collection",
    titulo: "O acervo inteiro, não uma seleção",
    texto:
      "Todo e-book do catálogo com arquivo disponível entra na assinatura. " +
      "Não é uma lista que gira a cada mês nem um pacote de títulos escolhidos: é o catálogo.",
  },
  {
    icone: "bi-bookmark-star",
    titulo: "Marcadores e anotações que ficam",
    texto:
      "Marque a página que importa, escreva a nota ao lado do trecho e reveja tudo depois, " +
      "organizado por livro. Fica guardado na sua conta, não no aparelho.",
  },
];

// As outras quatro são reais e valem ser ditas, mas nenhuma delas fecha uma
// venda sozinha. Ficam em linha compacta: presentes para quem lê tudo, sem
// disputar o olho de quem só passa.
const VANTAGENS_APOIO = [
  { icone: "bi-arrow-clockwise", titulo: "Continua de onde parou", texto: "O leitor guarda a página, no computador e no celular." },
  { icone: "bi-phone", titulo: "Lê bem no celular", texto: "Texto no tamanho da tela, sem dar zoom a cada página." },
  { icone: "bi-patch-check", titulo: "Selo de assinante", texto: "A plaquinha Assinante Insigne nas suas avaliações, e o selo Clube Insigne no seu perfil." },
  // Desde 10/09/2026 coin não paga o Clube direto: ela compra TICKET, e o
  // ticket é que vira dias. A frase antiga ("também assinam o Clube") seguia
  // verdadeira no resultado e errada no caminho — e quem procurasse o botão de
  // pagar com coins na página não o acharia mais.
  { icone: "bi-coin", titulo: "Dá para pagar com Coins", texto: <>As moedas que você ganha comprando e avaliando viram <TicketTxt />, e cada <TicketTxt um /> são 30 dias de Clube.</> },
];

const ClubeInsigne = () => {
  const { user, loading: authLoading } = useAuth();
  // Os números da vitrine chegam sem login — é o que a tela anônima usa, e é
  // também a rede de segurança do ramo logado quando `clube/status` falha.
  const vitrine = useClubeVitrine();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(null); // 'coins' | 'pagamento'
  const { confirmar, dialogo } = useConfirmarGasto();
  const [recentes, setRecentes] = useState([]);

  const carregar = useCallback(async () => {
    if (!user) { setCarregando(false); return; }
    try {
      const r = await fetch(`${API_URL}clube/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (r.ok) setStatus(await r.json());
    } catch (_) {
      /* a página ainda serve como vitrine mesmo sem o status */
    } finally {
      setCarregando(false);
    }
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  // OS LANÇAMENTOS RECENTES SÃO ARGUMENTO, NÃO ENFEITE, e por isso são buscados
  // nos dois ramos — inclusive para o visitante anônimo, que é justamente quem
  // precisa ver que existem livros atrás do número. Servem a duas coisas: as
  // capas da faixa e o preço de referência de UM título (ver medianaCentavos).
  // A rota é pública e a home já a usa; falhar aqui tira a faixa e a
  // comparação, nunca a oferta.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}ebooks-recentes`);
        if (!r.ok) return;
        const d = await r.json();
        if (vivo && Array.isArray(d)) setRecentes(d);
      } catch (_) {
        /* sem capas e sem comparação; o resto da página continua inteiro */
      }
    })();
    return () => { vivo = false; };
  }, []);

  // ============ COMPRAR E ENTRAR VIRARAM DOIS ATOS ============
  //
  // Até 10/09/2026 esta tela tinha dois botões de COMPRA (pagando e com coins),
  // e comprar ativava o Clube na hora. O problema era o mês seguinte: para
  // continuar lendo, a pessoa tinha de voltar aqui no dia certo — e quem
  // esquecia perdia o acesso.
  //
  // Agora o Clube se paga com TICKET, que se compra quando dá e se usa quando
  // se quer. Sobrou um botão só, e ele muda conforme o saldo: com ticket
  // guardado, "usar"; sem ticket, "comprar" — que leva à loja de créditos, no
  // perfil, onde estão os pacotes com desconto.
  const usarTicket = async () => {
    if (processando) return;
    // Usar um Ticket é gasto sem volta: confirma antes (ui/ConfirmarGasto).
    const ok = await confirmar(opcoesUsarTicket({
      saldoTickets: status?.saldo_tickets,
      dias: status?.dias_por_ticket || status?.dias || 30,
      fim: status?.fim,
      ativa: !!status?.ativa,
    }));
    if (!ok) return;
    setProcessando("ticket");
    try {
      const r = await fetch(`${API_URL}creditos/usar-no-clube`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ quantidade: 1 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || d?.message || "Não foi possível ativar o Clube.");
      message.success(`Clube ativo por mais ${d.dias_restantes} dias.`);
      await carregar();
      // O cabeçalho do perfil mostra o saldo de tickets; um a menos agora.
      window.dispatchEvent(new CustomEvent("creditosAtualizados"));
    } catch (e) {
      message.error(e.message);
    } finally {
      setProcessando(null);
    }
  };

  // ===== COMPRAR O PACOTE AQUI MESMO =====
  //
  // Mandar para a aba do perfil é um desvio de duas telas no momento exato em
  // que a pessoa decidiu. Da página de venda, o pacote vai direto para o
  // pagamento — o preço é o do servidor (a tela manda QUAL pacote, e só).
  const comprarPacote = async (tickets) => {
    if (processando) return;
    if (anonimo) return irParaLogin();
    setProcessando(`pacote-${tickets}`);
    try {
      const r = await fetch(`${API_URL}creditos/comprar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tipo: "ticket", quantidade: tickets }),
      });
      const d = await r.json();
      if (!r.ok || !d?.checkout_url) throw new Error(d?.error || d?.message || "Não foi possível gerar o pagamento.");
      window.location.href = d.checkout_url;
    } catch (e) {
      message.error(e.message);
      setProcessando(null);
    }
  };

  // A loja de créditos é uma ABA DO PERFIL, e o perfil não expõe a aba na URL:
  // a troca é feita por evento, como o resto do site já faz. O `setTimeout`
  // existe porque o Perfil só passa a ouvir depois de montar.
  const irParaAbaDoPerfil = (aba) => {
    navigate(main + "perfil");
    setTimeout(
      () => window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: aba })),
      150
    );
  };
  const irComprarTicket = () => irParaAbaDoPerfil("creditos");

  // "Escolher um livro" de quem assina desce até o acervo, que mora nesta mesma
  // página (ver AcervoDoClube). Antes levava a /livros — a vitrine de COMPRA,
  // com preço e carrinho em cada card, para quem já pode ler tudo.
  const irParaOAcervo = () => {
    document.getElementById("acervo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ============ OS TRÊS ESTADOS DA TELA, E POR QUE NÃO SÃO DOIS ============
  //
  // `carregando` sozinho não decide o que mostrar: o efeito deste componente
  // roda ANTES do efeito do AuthProvider, então a primeira passada vê
  // `user === null`, cai no atalho de `carregar()` e desliga o único estado de
  // carregamento que existe. Sem juntar `authLoading`, quem está logado veria a
  // oferta de visitante piscar antes da sua.
  //
  // Antes desta reescrita o ramo anônimo era um `return` inteiro e separado, com
  // metade do conteúdo da página. Era o avesso do que se quer: quem AINDA NÃO
  // assina é exatamente quem precisa de todos os argumentos. Agora a página é
  // uma só e o que troca é o cartão da direita.
  const anonimo = !authLoading && !user;
  const esperando = authLoading || (!!user && carregando);
  const ativo = !!status?.ativa;
  const saldo = Number(status?.saldo_coins) || 0;

  // ============ OS NÚMEROS CAEM PARA A VITRINE, E NUNCA PARA ZERO ============
  //
  // O ramo anônimo desta tela ganhou os números do servidor; o LOGADO lia só
  // `status`, que começa nulo e pode nunca chegar, porque não há retry. O
  // resultado era a tela de venda abrindo com "R$ 0,00" e "ou 500 Insigne
  // Coins ·  dias" — e ficando assim para sempre se `clube/status` falhasse.
  //
  // `vitrine` é pública, não depende de login e já está neste componente: ela é
  // a rede de segurança dos cinco números que a página vende.
  const precoCentavos = Number(status?.preco_centavos) || vitrine.precoCentavos;
  const precoCoins = Number(status?.preco_coins) || vitrine.precoCoins;
  const diasClube = Number(status?.dias) || vitrine.dias;
  const acervo = Number(status?.acervo_total) || vitrine.acervo;
  // PROVA SOCIAL, e o servidor decide se ela existe: `assinantes` já vem 0
  // quando o clube tem poucos membros — ver AssinaturaService::totalAtivos(). O
  // corte é lá e não aqui porque, feito aqui, o número verdadeiro viajaria no
  // JSON e bastaria abrir a aba de rede para lê-lo.
  const assinantes = Number(status?.assinantes) || vitrine.assinantes;

  // ===== O PREÇO POR MÊS DE CADA PACOTE =====
  //
  // É o argumento mais forte que o Clube ganhou com o Ticket, e a página não o
  // dizia: ela anunciava "R$ 50" enquanto o pacote de 12 sai bem mais barato
  // por mês. O "por mês" é calculado dos DIAS que o pacote dá, e não do número
  // de tickets: se um dia o ticket passar a valer 45 dias, a conta continua
  // certa sem ninguém lembrar de mexer aqui.
  const pacotes = useMemo(
    () =>
      (vitrine.pacotes || [])
        .map((p) => {
          const meses = Math.max(1, Math.round((Number(p.dias) || 0) / 30));
          return { ...p, meses, porMes: Math.round((Number(p.centavos) || 0) / meses) };
        })
        .filter((p) => p.porMes > 0),
    [vitrine.pacotes]
  );

  // O de menor preço por mês é o destacado. CALCULADO, e não "o de 12
  // tickets" escrito à mão: se o dono mudar os preços no painel, o selo de
  // "melhor custo" tem de ir sozinho para quem de fato for o melhor.
  const melhorPacote = pacotes.reduce(
    (m, p) => (!m || p.porMes < m.porMes ? p : m),
    null
  );
  // Só vale anunciar "a partir de" quando o melhor é mais barato que o avulso.
  const aPartirDe =
    melhorPacote && precoCentavos > 0 && melhorPacote.porMes < precoCentavos
      ? melhorPacote.porMes
      : 0;
  const temSaldo = precoCoins > 0 && saldo >= precoCoins;

  const capas = useMemo(
    () => (recentes || []).filter((l) => l?.image).slice(0, 8),
    [recentes]
  );
  const precoAvulso = useMemo(() => medianaCentavos(recentes), [recentes]);

  // Quantos e-books avulsos custariam o mesmo que a assinatura. `ceil` de
  // propósito: com títulos de R$ 12 contra uma assinatura de R$ 50, "pelo preço
  // de 4" seria falso (R$ 48). Arredondando para cima a frase é sempre
  // verdadeira, mesmo que a oferta pareça um pouco pior do que é — numa página
  // de venda, o número que se pode conferir vale mais que o número redondo.
  const equivalentes =
    precoAvulso > 0 && precoCentavos > 0
      ? Math.max(1, Math.ceil(precoCentavos / precoAvulso))
      : 0;
  // Divisão simples: assinatura ÷ acervo. Some quando arredonda para menos de
  // um centavo, porque "R$ 0,00 por livro" não é argumento, é erro de conta.
  const centavosPorLivro =
    acervo > 0 && precoCentavos > 0 ? Math.round(precoCentavos / acervo) : 0;

  const irParaLogin = () => navigate(main + "login");

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-20 text-white">
      <div className="max-w-[1040px] mx-auto">

        {/* ==================== O TOPO ====================
            Argumento à esquerda, oferta à direita. No celular vira uma coluna só
            e a ordem do DOM é a ordem da conversa: o que a assinatura é, depois
            quanto custa. */}
        <Reveal>
          <section
            className="relative isolate mt-6 md:mt-10 rounded-3xl border border-insigne-orange/30 overflow-hidden
                       bg-gradient-to-br from-insigne-orange/[0.14] via-insigne-navy-soft to-insigne-navy-soft
                       p-5 sm:p-7 md:p-10"
          >
            {/* Os anéis tracejados que giram, como nas aberturas do site
                principal. Atrás de tudo (`-z-10` dentro do `isolate`). */}
            <Giros className="-bottom-40 -left-32 -z-10 h-[26rem] w-[26rem]" />
            <div aria-hidden="true" className="textura-pontos pointer-events-none absolute inset-0 -z-10 opacity-60 [mask-image:linear-gradient(to_bottom_right,black,transparent_60%)]" />
            <div className="grid lg:grid-cols-[1.15fr_.95fr] gap-8 lg:gap-10 items-start">

              {/* ---------- o argumento ---------- */}
              <div className="min-w-0">
                <span className="inline-flex items-center gap-2 rounded-lg border border-insigne-orange/45
                                 bg-insigne-orange/10 px-3 py-1 text-xs font-bold uppercase tracking-wider
                                 text-insigne-orange">
                  <i className="bi bi-stars" /> Clube Insigne
                </span>

                {ativo ? (
                  <>
                    <h1 className="titulo-display mb-0 mt-[1rem] text-[3rem] sm:text-[4rem] md:text-[4.5rem]">
                      Sua assinatura está ativa
                    </h1>
                    <p className="text-white/65 mt-4 text-base md:text-lg leading-relaxed max-w-xl">
                      {acervo > 0
                        ? `Você pode abrir qualquer um dos ${acervo} e-books do acervo agora mesmo, `
                        : "Você pode abrir qualquer e-book do acervo agora mesmo, "}
                      direto no site, e o leitor guarda a página onde você parar.
                    </p>
                  </>
                ) : (
                  <>
                    {/* O NÚMERO DO ACERVO EM TAMANHO DE TÍTULO.
                        Ele é o argumento mais forte da oferta e vivia numa linha
                        de rodapé cinza ("199 livros liberados."). Aqui é a
                        primeira coisa que a página diz. Quando o servidor não
                        sabe o total, a manchete cai para uma frase verdadeira em
                        vez de um número inventado — ver totalDoAcervo(), que
                        devolve 0 quando a contagem falha. */}
                    <h1 className="titulo-display mb-0 mt-[1rem] text-[3.2rem] sm:text-[4.3rem] md:text-[5rem]">
                      {acervo > 0 ? (
                        <>
                          <span className="text-insigne-orange">{acervo} e-books</span>
                          <br />por uma assinatura só
                        </>
                      ) : (
                        <>O acervo inteiro<br />por uma assinatura só</>
                      )}
                    </h1>
                    <p className="text-white/65 mt-4 text-base md:text-lg leading-relaxed max-w-xl">
                      Qualquer e-book do catálogo, lido aqui no site, com marcador de página,
                      anotações e a memória de onde você parou, no computador e no celular.
                    </p>
                  </>
                )}

                <div className="mt-6 flex flex-wrap gap-2">
                  {diasClube > 0 && (
                    <Chip icone="bi-calendar-check">{diasClube} dias de leitura</Chip>
                  )}
                  {!ativo && aPartirDe > 0 && (
                    <Chip icone="bi-tag-fill">a partir de {reais(aPartirDe)}/mês</Chip>
                  )}
                  <Chip icone="bi-shield-check">Não renova sozinha</Chip>
                  <Chip icone="bi-window">Lê no navegador, sem instalar nada</Chip>
                  {assinantes > 0 && (
                    <Chip icone="bi-people-fill">{assinantes} pessoas já leem pelo Clube</Chip>
                  )}
                </div>

                <FaixaDeCapas capas={capas} />
              </div>

              {/* ---------- a oferta ---------- */}
              <div className="min-w-0">
                {esperando ? (
                  <EsqueletoDaOferta />
                ) : ativo ? (
                  <CardAtivo
                    status={status}
                    diasClube={diasClube}
                    onEscolherLivro={irParaOAcervo}
                    onMinhasLeituras={() => irParaAbaDoPerfil("meus-livros")}
                    onMeuClube={() => irParaAbaDoPerfil("clube")}
                  />
                ) : (
                  <CardOferta
                    anonimo={anonimo}
                    aPartirDe={aPartirDe}
                    precoCentavos={precoCentavos}
                    precoCoins={precoCoins}
                    diasClube={diasClube}
                    status={status}
                    temSaldo={temSaldo}
                    processando={processando}
                    onUsarTicket={usarTicket}
                    onComprarTicket={irComprarTicket}
                    onLogin={irParaLogin}
                  />
                )}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==================== A CONTA ====================
            A comparação de preço existia só na cabeça de quem escreveu a página:
            "R$ 50 é menos que um livro" era implícito. Aqui ela é explícita, e os
            dois valores são de verdade — a assinatura vem do config e o e-book
            avulso é a mediana dos lançamentos que a página já busca.
            O bloco inteiro some se faltar qualquer um dos dois: meia comparação é
            pior que nenhuma. E não aparece para quem já assina — quem já pagou
            não precisa ser convencido do preço de novo. */}
        {!ativo && precoCentavos > 0 && precoAvulso > 0 && (
          <Reveal>
            <section className="mt-5 bloco p-5 sm:p-7">
              <h2 className="titulo-display mb-0 text-[2rem] sm:text-[2.5rem] flex items-center gap-3 [&>i]:text-[1.35rem]">
                <i className="bi bi-calculator text-insigne-orange" /> A conta, sem rodeio
              </h2>

              <div className="mt-5 grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="rounded-2xl border border-line bg-insigne-navy-deep/60 p-5">
                  <p className="text-sm text-white/55">Comprando um e-book</p>
                  <p className="text-3xl font-extrabold mt-1 leading-none">{reais(precoAvulso)}</p>
                  {/* "Preço do título do meio" era a mediana explicada com a
                      palavra errada: descrevia a CONTA, não o valor, e o dono
                      leu como frase quebrada (15/09/2026). "Típico" diz a mesma
                      coisa e é honesto — continua sem prometer "média da
                      editora", que seria mentira: a amostra é só dos
                      lançamentos recentes. */}
                  <p className="text-xs text-white/45 mt-3 leading-relaxed">
                    É o preço típico de um e-book entre os lançamentos recentes.
                    Você leva <strong className="text-white/70">um livro</strong>, e ele fica seu para sempre.
                  </p>
                </div>

                <div className="rounded-2xl border border-insigne-orange/40 bg-insigne-orange/[0.08] p-5">
                  <p className="text-sm text-white/55">Assinando o Clube</p>
                  <p className="text-3xl font-extrabold text-insigne-orange mt-1 leading-none">
                    {reais(precoCentavos)}
                  </p>
                  <p className="text-xs text-white/55 mt-3 leading-relaxed">
                    Você lê{" "}
                    <strong className="text-white/80">
                      {acervo > 0 ? `os ${acervo} e-books do acervo` : "o acervo inteiro"}
                    </strong>
                    {diasClube > 0 ? ` por ${diasClube} dias.` : "."}
                  </p>
                </div>
              </div>

              <p className="mt-4 rounded-2xl border border-insigne-orange/25 bg-insigne-orange/[0.06]
                            px-5 py-4 text-center text-base sm:text-lg leading-relaxed">
                Pelo preço de{" "}
                <strong className="text-insigne-orange">
                  {equivalentes === 1 ? "um e-book avulso" : `${equivalentes} e-books avulsos`}
                </strong>
                , você lê {acervo > 0 ? <strong>{acervo}</strong> : "o acervo inteiro"}
                {centavosPorLivro > 0 && (
                  <>, o que dá <strong className="text-insigne-orange">{reais(centavosPorLivro)}</strong> por livro</>
                )}
                .
              </p>
            </section>
          </Reveal>
        )}

        {pacotes.length > 0 && (
          <PacotesDoClube
            pacotes={pacotes}
            melhor={melhorPacote}
            ativo={ativo}
            processando={processando}
            onComprar={comprarPacote}
          />
        )}

        {/* Com a assinatura ATIVA, "o que a assinatura dá" vira o próprio
            acervo — quem já assina não precisa ser convencido, precisa dos
            livros (ver AcervoDoClube). `!esperando`: enquanto o status não
            volta, nenhum dos dois, senão o assinante via as vantagens
            aparecerem e trocarem pelo acervo um instante depois. */}
        {ativo ? <AcervoDoClube /> : !esperando && <Vantagens />}

        {dialogo}

        {/* Honestidade sobre o que a assinatura NÃO é. Descobrir isso depois de
            pagar é a fonte número um de pedido de estorno. */}
        <Reveal>
          <section className="mt-5 bloco p-5 sm:p-6">
            <h2 className="titulo-display text-[1.9rem] flex items-center gap-3 mb-[0.9rem] [&>i]:text-[1.25rem]">
              <i className="bi bi-info-circle text-insigne-orange" /> O que é bom saber antes
            </h2>
            <ul className="text-sm text-white/60 space-y-2 leading-relaxed">
              <li>
                <strong className="text-white/80">Assinar é ler, não é ficar com o arquivo.</strong>{" "}
                A leitura acontece aqui no site. O download do PDF continua sendo de quem compra o livro.
              </li>
              <li>
                <strong className="text-white/80">Suas anotações são suas.</strong>{" "}
                Se a assinatura vencer, elas continuam guardadas e voltam exatamente onde estavam se você renovar.
              </li>
              <li>
                <strong className="text-white/80">Os livros que você já comprou não mudam.</strong>{" "}
                Eles seguem na sua conta, para ler e baixar, com ou sem assinatura.
              </li>
            </ul>
          </section>
        </Reveal>

        {/* ==================== O FECHAMENTO ====================
            A página ficou longa e no celular ela é uma coluna só: quem terminou
            de ler os argumentos está a quatro telas do botão do topo. Sem este
            bloco, decidir sim custa rolar de volta — e rolar de volta é onde a
            venda se perde. Não aparece para quem já assina. */}
        {!ativo && !esperando && (
          <Reveal>
            <section className="mt-5 rounded-3xl border border-insigne-orange/30 p-6 md:p-9 text-center
                                bg-gradient-to-br from-insigne-orange/[0.14] via-insigne-navy-soft to-insigne-navy-soft">
              <h2 className="titulo-display mb-0 text-[2.6rem] md:text-[3.4rem]">
                {acervo > 0 ? <>Comece pelos {acervo} livros de hoje</> : <>Comece pelo acervo de hoje</>}
              </h2>
              <p className="text-white/60 mt-3 max-w-md mx-auto leading-relaxed">
                {diasClube > 0
                  ? `São ${diasClube} dias de leitura, uma cobrança só, e você decide se volta.`
                  : "Uma cobrança só, e você decide se volta."}
              </p>

              <button
                onClick={anonimo ? irParaLogin : irComprarTicket}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-insigne-orange
                           px-7 py-3.5 font-bold text-paper hover:bg-insigne-orange-dark foco-laranja
                           transition-colors"
              >
                <i className={"bi " + (anonimo ? "bi-box-arrow-in-right" : "bi-ticket-perforated-fill")} />
                {anonimo
                  ? "Entrar para comprar"
                  : precoCentavos > 0
                    ? `Comprar Ticket · ${reais(precoCentavos)}`
                    : "Comprar Ticket"}
              </button>

              <p className="text-xs text-white/45 mt-4">
                <i className="bi bi-shield-check text-insigne-orange" />{" "}
                <strong>Não renova sozinha.</strong> Nada é cobrado automaticamente.
              </p>

              <Link to={main + "livros"} className="link-sublinhado text-sm inline-block mt-5 text-white/60">
                Antes, ver o catálogo
              </Link>
            </section>
          </Reveal>
        )}
      </div>
    </div>
  );
};

// Selo de uma linha do topo. `rounded-lg` e não `rounded-full`: os adicionais
// visuais do site estão indo para o formato menos arredondado.
const Chip = ({ icone, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-insigne-navy/40
                   px-2.5 py-1.5 text-xs sm:text-[13px] text-white/70">
    <i className={"bi " + icone + " text-insigne-orange"} />
    {children}
  </span>
);

// CAPAS DE VERDADE. "199 e-books" é um número; a fileira de capas é a prova de
// que existem livros atrás dele — e numa editora a capa é o produto.
//
// Elas ficam sobrepostas, o que diz "tem mais" sem precisar de um texto dizendo
// "e outros 191". Não são links de propósito: daqui a pessoa deve decidir
// assinar, não sair para a página de um título. Por isso também são
// `aria-hidden` — são ilustração, e um leitor de tela anunciando oito imagens
// sem legenda só atrapalha.
//
// Abaixo de quatro capas a faixa não aparece: uma fileira curta parece acervo
// pequeno, o oposto do que ela existe para dizer.
const FaixaDeCapas = ({ capas }) => {
  if (!capas || capas.length < 4) return null;

  return (
    <div className="mt-7 flex -space-x-4 sm:-space-x-5" aria-hidden="true">
      {capas.map((livro, i) => (
        <img
          key={livro.id}
          src={`${API_URL}public/${livro.image}`}
          alt=""
          loading="lazy"
          className={
            "w-[60px] h-[88px] sm:w-[78px] sm:h-[114px] shrink-0 rounded-md object-cover " +
            "border-2 border-insigne-navy-soft shadow-lg " +
            // No celular só cabem seis sem espremer; as últimas entram a partir
            // do sm, onde há largura para elas.
            (i >= 6 ? "hidden sm:block" : "")
          }
        />
      ))}
    </div>
  );
};

// Enquanto não se sabe se a pessoa assina, o cartão da direita não pode sumir:
// o topo colapsaria de duas colunas para uma e voltaria a duas, empurrando a
// página inteira para baixo depois de ela já ter começado a ler. Esqueleto no
// tamanho do cartão real.
const EsqueletoDaOferta = () => (
  <div className="rounded-2xl border border-line bg-insigne-navy-deep/70 p-5 sm:p-6">
    <div className="h-4 w-32 rounded bg-white/10 animate-pulse" />
    <div className="h-11 w-44 mt-3 rounded-lg bg-white/10 animate-pulse" />
    <div className="h-4 w-40 mt-3 rounded bg-white/10 animate-pulse" />
    <div className="h-[52px] w-full mt-6 rounded-xl bg-white/10 animate-pulse" />
    <div className="h-[52px] w-full mt-3 rounded-xl bg-white/[0.06] animate-pulse" />
  </div>
);

const CardOferta = ({
  anonimo, aPartirDe, precoCentavos, precoCoins, diasClube, status,
  temSaldo, processando, onUsarTicket, onComprarTicket, onLogin,
}) => (
  // `cantoneiras`: as quinas laranja do site do Congresso, no que é o destaque
  // da tela. Ver App.css.
  <div className="cantoneiras rounded-2xl border border-line bg-insigne-navy-deep/70 p-5 sm:p-6">
    <p className="text-sm text-white/50">Assinatura do Clube</p>

    {/* O PREÇO APARECE ANTES DO LOGIN. Pedir para a pessoa entrar na conta para
        só então descobrir quanto custa é o jeito mais rápido de perdê-la: quem
        não sabe o preço não tem motivo para fazer login.
        Enquanto ele não chega vai uma barra, e não um traço: o valor estava
        escrito à mão antes de 03/09/2026 e aparecia instantâneo; trocar por dado
        do servidor sem tratar o carregamento abriria a tela mais importante do
        funil mostrando "—" onde estava o preço. Falha de rede também cai aqui e
        a barra fica — melhor um vazio que se explica do que um traço que parece
        defeito. */}
    {precoCentavos > 0 ? (
      <p className="text-4xl sm:text-5xl font-extrabold text-insigne-orange mt-1 leading-none">
        {reais(precoCentavos)}
      </p>
    ) : (
      <div className="h-11 sm:h-12 w-40 mt-1 rounded-lg bg-white/10 animate-pulse" />
    )}

    <p className="text-sm text-white/55 mt-2.5">
      {precoCoins > 0 && (
        <>ou <strong className="text-white/85">{precoCoins} Insigne Coins</strong> · </>
      )}
      {diasClube > 0 ? `${diasClube} dias de leitura` : "por assinatura"}
    </p>

    {/* O GANCHO DO PACOTE, no card onde a pessoa decide. Leva à seção dos
        pacotes, mais abaixo, em vez de só prometer: "a partir de" sem mostrar
        de onde vem é o tipo de frase em que ninguém acredita. */}
    {aPartirDe > 0 && (
      <a
        href="#pacotes"
        className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-insigne-ticket/40
                   bg-insigne-ticket/10 px-3.5 py-2.5 text-sm transition-colors hover:bg-insigne-ticket/15"
      >
        <span className="text-white/75">
          Em pacote, a partir de{" "}
          <strong className="text-insigne-ticket">{reais(aPartirDe)}/mês</strong>
        </span>
        <i className="bi bi-arrow-down text-insigne-ticket" />
      </a>
    )}

    {anonimo ? (
      <>
        <button
          onClick={onLogin}
          className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-xl
                     bg-insigne-orange px-5 py-3.5 font-bold text-paper foco-laranja
                     hover:bg-insigne-orange-dark transition-colors"
        >
          <i className="bi bi-box-arrow-in-right" /> Entrar para assinar
        </button>
        <p className="text-xs text-white/45 mt-2.5 text-center leading-relaxed">
          Este é o preço final: nada muda depois do login.
        </p>
      </>
    ) : (
      <Botoes
        status={status}
        diasPorTicket={diasClube}
        processando={processando}
        onUsarTicket={onUsarTicket}
        onComprarTicket={onComprarTicket}
      />
    )}

    <SeloNaoRenova dias={diasClube} />
  </div>
);

const CardAtivo = ({ status, diasClube, onEscolherLivro, onMinhasLeituras, onMeuClube }) => (
  <div className="rounded-2xl border border-insigne-orange/40 bg-insigne-orange/[0.07] p-5 sm:p-6">
    <p className="text-sm text-white/55 flex items-center gap-2">
      <i className="bi bi-patch-check-fill text-insigne-orange" /> Vale até
    </p>
    <p className="text-3xl sm:text-4xl font-extrabold text-insigne-orange mt-1 leading-none">
      {fmtData(status.fim)}
    </p>
    {status.dias_restantes > 0 && (
      <p className="text-sm text-white/60 mt-2.5">
        Faltam <strong className="text-white/85">{status.dias_restantes}</strong>{" "}
        {status.dias_restantes === 1 ? "dia" : "dias"} de leitura.
      </p>
    )}

    {/* ===== AS MEDIDAS DO BOTÃO =====
        Era `className="btn-solido inline-flex"` e nada mais. `.btn-solido`
        (App.css) entrega fundo, borda tracejada e a inversão do hover — e SÓ
        isso: nem padding, nem raio, nem peso de fonte, nem espaço entre o
        ícone e o texto. O resultado era um retângulo laranja colado no próprio
        rótulo, com o livrinho grudado na primeira letra. É o mesmo defeito que
        a aba do Clube no perfil já tinha, e a correção é a mesma. */}
    {/* ===== OS TRÊS CAMINHOS DE QUEM JÁ ASSINA =====
        A arrumação de 11/09/2026, depois de o dono estranhar a anterior: ela
        era uma grade de dois com um terceiro botão atravessado embaixo, e o
        atravessado (o vermelho) acabava sendo o mais largo da caixa — a coisa
        MENOS urgente com o maior peso. Os rótulos ainda quebravam em duas
        linhas dentro das meias-larguras.

        Agora a hierarquia é a da pergunta que a pessoa faz aqui:
          1. "o que eu leio?" ....... botão inteiro, laranja, sozinho na linha
          2. "onde eu parei?" / "como está minha assinatura?" .... o par abaixo,
             ambos discretos e do mesmo tamanho, porque são consultas.

        O vermelho do Ticket fica no contorno e no texto de "Meu Clube": ele diz
        de que assunto é o botão sem competir com o laranja da ação principal. */}
    <div className="mt-5 space-y-2">
      <button
        type="button"
        onClick={onEscolherLivro}
        className="btn-solido flex w-full items-center justify-center gap-2
                   rounded-lg px-4 py-3 text-sm font-bold"
      >
        <i className="bi bi-book" /> Escolher um livro
      </button>

      <div className="grid grid-cols-2 gap-2">
        {/* Contorno discreto: subordinadas à ação principal sem sumir. A
            primeira vai direto à estante (aba E-books), e não à aba "Dados". */}
        <button
          type="button"
          onClick={onMinhasLeituras}
          className="foco-laranja inline-flex items-center justify-center gap-1.5 rounded-lg
                     border border-white/15 bg-white/5 px-2 py-2.5 text-[13px] font-semibold
                     leading-tight text-white/85 transition-colors hover:bg-white/10 hover:border-white/30"
        >
          <i className="bi bi-bookmark-check shrink-0" /> Minhas leituras
        </button>

        <button
          type="button"
          onClick={onMeuClube}
          title="Ver e renovar o seu Clube"
          className="foco-laranja inline-flex items-center justify-center gap-1.5 rounded-lg
                     border border-insigne-ticket/45 bg-insigne-ticket/[0.08] px-2 py-2.5 text-[13px]
                     font-semibold leading-tight text-insigne-ticket transition-colors
                     hover:bg-insigne-ticket/15 hover:border-insigne-ticket/70"
        >
          <i className="bi bi-ticket-perforated-fill shrink-0" /> Meu Clube
        </button>
      </div>
    </div>

    <SeloNaoRenova dias={diasClube} />

  </div>
);

// "NÃO RENOVA SOZINHA" SUBIU DO RODAPÉ PARA DEBAIXO DO BOTÃO.
//
// É a pergunta que trava a assinatura — "isso vai me cobrar todo mês?" — e ela
// era respondida na última linha da página, em cinza de 12px: depois da decisão,
// portanto tarde demais. Aqui é o último texto lido antes do clique. O custo é
// ocupar espaço nobre do cartão; vale, porque é o argumento que a maior parte da
// concorrência de assinatura não consegue dar.
/** "1 mês", "3 meses", "1 ano" — o prazo lido como gente fala. */
const prazo = (meses) => (meses === 12 ? "1 Ano" : meses === 1 ? "1 Mês" : `${meses} Meses`);

/**
 * Os pacotes de Ticket, na página de venda.
 *
 * ================= O NÚMERO GRANDE É O "POR MÊS" =================
 *
 * O total de um pacote de 12 é o MAIOR número da prateleira, e mostrado grande
 * ele assusta: R$ 400 parece mais caro que R$ 50. O que o pacote tem de bom é o
 * contrário — ele sai mais BARATO por mês — então é esse o número grande, e o
 * total vai logo abaixo, menor, junto com quanto se economiza.
 *
 * ================= COMPRAR MAIS NÃO É SÓ PARA QUEM ENTRA =================
 *
 * A seção aparece também para quem já assina: os dias do ticket SOMAM ao fim
 * atual, então comprar um pacote agora é garantir os próximos meses mais barato
 * — e a pessoa não perde nada do que já pagou.
 */
const PacotesDoClube = ({ pacotes, melhor, ativo, processando, onComprar }) => (
  <Reveal>
    {/* ESPAÇAMENTO NO CELULAR (17/09/2026): `p-5` e `p-4` são homônimos do
        Bootstrap (3rem e 1,5rem, com !important). A 390px sobravam ~110px de
        largura por pacote, e o print mostrou o preço quebrado em "R$ / 50,00",
        o botão cortado em "Compra" e a tarja de desconto por cima do selo.
        Os valores daqui não têm homônimo; no computador o respiro é o mesmo. */}
    <section id="pacotes" className="mt-5 scroll-mt-24 rounded-3xl border border-insigne-ticket/30 p-[1.125rem] sm:p-7
                                      bg-gradient-to-br from-insigne-ticket/[0.10] via-insigne-navy-soft to-insigne-navy-soft">
      <h2 className="titulo-display mb-0 text-[2rem] sm:text-[2.5rem] flex items-center gap-3 [&>i]:text-[1.35rem]">
        <i className="bi bi-ticket-perforated-fill text-insigne-ticket" />
        {ativo ? "Garanta os próximos meses mais barato" : "Quanto mais tempo, menos por mês"}
      </h2>
      <p className="mt-1.5 text-sm text-white/60 leading-relaxed max-w-2xl">
        Cada <TicketTxt um /> vale 30 dias de Clube. Compre em pacote e use quando quiser: eles ficam
        guardados na sua conta, e os dias{" "}
        <strong className="text-white/85">somam</strong> ao que você já tem.
      </p>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 orfa-ao-centro-ate-lg">
        {pacotes.map((p, i) => {
          const destaque = melhor && p.id === melhor.id && pacotes.length > 1;
          const ocupado = processando === `pacote-${p.tickets}`;
          return (
            <div
              key={p.id}
              className={
                "relative flex flex-col rounded-2xl border px-3.5 pb-5 pt-5 sm:px-5 sm:py-6 " +
                (destaque
                  ? "border-insigne-ticket bg-insigne-ticket/[0.12] shadow-[0_0_0_1px_rgb(var(--c-ticket)/0.4)]"
                  : "border-line bg-insigne-navy-deep/60")
              }
            >
              {/* A tarja de desconto no canto superior esquerdo, inclinada —
                  a MESMA da loja de créditos do perfil (pedido de 11/09/2026).
                  Ela era uma plaquinha pequena no meio do card, embaixo do
                  total, e passava despercebida entre as linhas de preço. */}
              <TarjaDesconto pct={p.desconto_percentual} indice={i} />

              {/* "Melhor custo" desceu para o PÉ do cartão, centrado
                  (17/09/2026). No alto à direita ele ainda colidia com a tarja
                  de desconto inclinada quando o cartão ficava estreito (o
                  print do celular mostrou os dois empilhados). No pé não
                  disputa lugar com nada — é onde a Loja de Créditos já o põe. */}
              {destaque && (
                <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-selo
                                 bg-insigne-ticket px-2 py-0.5 text-[10px] font-extrabold uppercase
                                 tracking-wide text-insigne-ticket-contraste">
                  Melhor custo
                </span>
              )}

              <p className="text-sm font-bold text-white">
                {p.tickets} <TicketTxt um={p.tickets === 1} />
              </p>
              <p className="text-xs text-white/50">{prazo(p.meses)} de Clube</p>

              {/* O "por mês", grande. */}
              <p className="mt-2.5 leading-none">
                <span className="whitespace-nowrap text-xl sm:text-3xl font-extrabold text-insigne-ticket">
                  {reais(p.porMes)}
                </span>
                <span className="text-xs text-white/55">/mês</span>
              </p>

              {/* O total, menor, com o riscado.
                  A linha "R$ X a menos por mês" saiu a pedido do dono em
                  11/09/2026: com o "por mês" grande logo acima e a tarja de
                  desconto no canto, era a terceira vez que o card dizia a mesma
                  economia — e o que mais esticava o card para baixo. */}
              <p className="mt-2 text-xs text-white/55">
                {reais(p.centavos)} no total
                {p.de_centavos > p.centavos && (
                  <span className="ml-1.5 text-white/35 line-through">{reais(p.de_centavos)}</span>
                )}
              </p>

              {/* `mt-auto` no invólucro: empurra o botão para o pé do card, e
                  os botões da fileira ficam na mesma linha mesmo quando um card
                  tem o riscado e o vizinho não. */}
              <div className="mt-auto pt-3.5">
                <button
                  onClick={() => onComprar(p.tickets)}
                  disabled={!!processando}
                  className={
                    "w-full rounded-xl px-3 py-2 text-sm font-bold transition-colors " +
                    "disabled:opacity-50 disabled:cursor-wait " +
                    (destaque
                      ? "bg-insigne-ticket text-insigne-ticket-contraste hover:opacity-90"
                      : "border border-insigne-ticket/50 text-insigne-ticket hover:bg-insigne-ticket/10")
                  }
                >
                  {ocupado ? "Abrindo pagamento..." : "Comprar"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-white/45 leading-relaxed">
        <i className="bi bi-shield-check text-insigne-ticket" /> Nenhum <TicketTxt um /> é usado sozinho: você
        decide quando cada um vira 30 dias. Tem <CoinsTxt />? Elas também compram <TicketTxt />, na{" "}
        <strong className="text-white/65">Loja de Créditos</strong> do seu perfil.
      </p>
    </section>
  </Reveal>
);

const SeloNaoRenova = ({ dias }) => (
  <div className="mt-4 rounded-xl border border-insigne-orange/25 bg-insigne-orange/[0.07] p-3.5">
    <p className="text-sm font-bold flex items-center gap-2">
      <i className="bi bi-shield-check text-insigne-orange" /> Não renova sozinha
    </p>
    <p className="text-xs text-white/60 mt-1.5 leading-relaxed">
      Você paga uma vez{dias > 0 ? `, lê ${dias} dias` : ""} e pronto. Nada é cobrado
      automaticamente. Quando estiver acabando, avisamos por e-mail e você decide se volta.
    </p>
  </div>
);

// AS SEIS CAIXAS IGUAIS VIRARAM DUAS + QUATRO.
//
// Elas eram uma grade de seis cartões idênticos: mesmo tamanho, mesmo ícone,
// mesma cor. Seis coisas com o mesmo peso não formam uma lista de vantagens,
// formam uma parede — o olho não sabe por onde entrar e pula tudo. Agora as duas
// que sustentam a oferta ocupam cartão grande e as outras quatro ficam em linha
// compacta. O custo é a página ficar mais alta; o ganho é ela ser lida.
const Vantagens = () => (
  <section className="mt-5">
    <Reveal>
      <h2 className="titulo-display mb-0 text-[2rem] sm:text-[2.5rem] flex items-center gap-3 [&>i]:text-[1.35rem]">
        <i className="bi bi-check2-circle text-insigne-orange" /> O que a assinatura dá
      </h2>
    </Reveal>

    <div className="mt-5 grid md:grid-cols-2 gap-4">
      {VANTAGENS_DESTAQUE.map((v) => (
        <Reveal key={v.titulo}>
          <div className="h-full rounded-2xl border border-insigne-orange/25 p-5 sm:p-6
                          bg-gradient-to-br from-insigne-orange/[0.10] via-insigne-navy-soft to-insigne-navy-soft">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl
                             bg-insigne-orange/15 border border-insigne-orange/30">
              <i className={"bi " + v.icone + " text-xl text-insigne-orange"} />
            </span>
            <h3 className="text-lg font-bold mt-4">{v.titulo}</h3>
            <p className="text-sm text-white/60 mt-2 leading-relaxed">{v.texto}</p>
          </div>
        </Reveal>
      ))}
    </div>

    <RevealStagger className="mt-3 grid sm:grid-cols-2 gap-3">
      {VANTAGENS_APOIO.map((v) => (
        <RevealStagger.Item key={v.titulo}>
          <div className="h-full flex gap-3 cartao p-4">
            <i className={"bi " + v.icone + " text-insigne-orange text-lg shrink-0 mt-0.5"} />
            <div className="min-w-0">
              <h3 className="font-bold text-sm">{v.titulo}</h3>
              <p className="text-xs text-white/55 mt-1 leading-relaxed">{v.texto}</p>
            </div>
          </div>
        </RevealStagger.Item>
      ))}
    </RevealStagger>
  </section>
);

// UM BOTÃO SÓ, e ele responde ao saldo.
//
// Eram dois de COMPRA ("Assinar pagando" e "Usar N coins"), porque comprar e
// entrar no Clube eram o mesmo ato. Com o Ticket eles se separaram: quem tem
// ticket guardado USA; quem não tem vai COMPRAR, na loja de créditos do perfil,
// onde estão os pacotes com desconto que esta tela não tem espaço para mostrar.
//
// O segundo botão continua existindo quando há ticket — "comprar mais" —, mas
// discreto: quem já pode entrar não deve ser empurrado para a loja primeiro.
const Botoes = ({ status, diasPorTicket, processando, onUsarTicket, onComprarTicket, compacto }) => {
  const tickets = Number(status?.saldo_tickets) || 0;
  const dias = Number(diasPorTicket) || Number(status?.dias_por_ticket) || 30;
  const temTicket = tickets > 0;

  return (
    <div className={compacto ? "mt-3 space-y-2" : "mt-5 space-y-3"}>
      {temTicket ? (
        <button
          onClick={onUsarTicket}
          disabled={!!processando}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-insigne-orange
                     px-5 py-3.5 font-bold text-paper hover:bg-insigne-orange-dark foco-laranja
                     disabled:opacity-50 disabled:cursor-wait transition-colors"
        >
          <i className={"bi " + (processando === "ticket" ? "bi-arrow-repeat animate-spin" : "bi-ticket-perforated-fill")} />
          {processando === "ticket" ? "Ativando..." : `Usar 1 Ticket · ${dias} dias`}
        </button>
      ) : (
        <button
          onClick={onComprarTicket}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-insigne-orange
                     px-5 py-3.5 font-bold text-paper hover:bg-insigne-orange-dark foco-laranja
                     transition-colors"
        >
          <i className="bi bi-ticket-perforated-fill" />
          Comprar Ticket
        </button>
      )}

      {temTicket && (
        <>
          <p className="text-center text-xs text-white/45">
            Você tem <strong className="text-insigne-ticket">{tickets}</strong>{" "}
            <TicketTxt um={tickets === 1} /> · {tickets * dias} dias guardados
          </p>
          <button
            onClick={onComprarTicket}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border
                       border-insigne-orange/50 bg-insigne-orange/10 px-5 py-2.5 text-sm font-bold
                       text-insigne-orange hover:bg-insigne-orange/20 foco-laranja transition-colors"
          >
            <i className="bi bi-plus-lg" /> Comprar mais
          </button>
        </>
      )}
    </div>
  );
};

export default ClubeInsigne;
